import React from "react";

import { useWsNavigate } from "../../hooks/useWsNavigate";
import {
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  Loader2,
  Search,
  Star,
  Pencil,
  MoreVertical,
  RotateCw,
  ThumbsDown,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

/**
 * Resolves the primary display value and pass/fail status from a single raw value.
 */
const resolveOneValue = ({ resultType, raw }) => {
  if (raw == null) return { value: null, passed: null, failed: null };

  const str = String(raw).toLowerCase();

  const isNA = str === "not_applicable" || str === "n/a" || str === "na";
  if (isNA) return { value: "N/A", passed: null, failed: null, notApplicable: true };

  const isPass = str === "pass" || str === "passed" || str === "true" || str === "yes";
  const isFail = str === "fail" || str === "failed" || str === "false" || str === "no";

  switch (resultType) {
    case "boolean":
      if (isPass || isFail) {
        return { value: isPass ? "Pass" : "Fail", passed: isPass, failed: isFail };
      }
      // Non-boolean value on a boolean metric — display as-is
      return { value: raw, passed: false, failed: false };
    case "rating": {
      const num = Number(raw);
      return {
        value: num,
        passed: num >= 4,
        failed: num <= 2,
      };
    }
    case "numeric": {
      const num = Number(raw);
      return {
        value: Number.isFinite(num)
          ? num % 1 === 0
            ? String(num)
            : num.toFixed(2)
          : String(raw),
        passed: null,
        failed: null,
      };
    }
    case "enum":
      return { value: String(raw), passed: null, failed: null };
    default: {
      return {
        value: raw,
        passed: isPass,
        failed: isFail,
      };
    }
  }
};

/**
 * Resolves the primary display value and pass/fail status from a metric result.
 * Also extracts sub-results for multi-value metrics.
 */
const resolveResult = ({ resultType, metricResult }) => {
  if (Array.isArray(metricResult) && metricResult.length > 0 && metricResult[0]?.metric_name != null) {
    const primary = resolveOneValue({ resultType, raw: metricResult[0]?.metric_value });
    const subResults = metricResult.map((item) => ({
      name: item.metric_name,
      outcomeClassification: item.outcome_classification,
      resultType,
      ...resolveOneValue({ resultType, raw: item.metric_value }),
    }));
    return { ...primary, subResults };
  }

  const raw = Array.isArray(metricResult)
    ? metricResult[0]?.metric_value
    : metricResult;
  const outcomeClassification = Array.isArray(metricResult)
    ? metricResult[0]?.outcome_classification
    : null;

  return { ...resolveOneValue({ resultType, raw }), subResults: null, outcomeClassification };
};

/* ─── Value display components ─── */

const BooleanBadge = ({ passed, failed, value }) => {
  if (passed)
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-green-50 text-green-700 font-semibold border border-green-200">
        <CheckCircle2 className="h-3 w-3" strokeWidth={2.5} />
        Pass
      </span>
    );
  if (failed)
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-red-50 text-red-700 font-semibold border border-red-200">
        <XCircle className="h-3 w-3" strokeWidth={2.5} />
        Fail
      </span>
    );
  return (
    <span className="inline-flex items-center text-xs px-2.5 py-1 rounded-full bg-muted text-foreground font-semibold border border-border">
      {value ?? "—"}
    </span>
  );
};

const RatingDisplay = ({ value }) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return <span className="text-xs text-muted-foreground">—</span>;
  const max = 5;
  return (
    <div className="inline-flex items-center gap-0.5">
      {[...Array(max)].map((_, i) => (
        <Star
          key={i}
          className={`h-3.5 w-3.5 ${
            i < num ? "text-amber-400 fill-amber-400" : "text-gray-300"
          }`}
          strokeWidth={1.5}
        />
      ))}
      <span className="text-xs font-semibold text-foreground ml-1">{num}/{max}</span>
    </div>
  );
};

const NumericDisplay = ({ value }) => (
  <span className="inline-flex items-center text-xs px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 font-semibold border border-blue-200">
    Value: {value}
  </span>
);

const EnumDisplay = ({ value }) => (
  <span className="inline-flex items-center text-xs px-2.5 py-1 rounded-full bg-violet-50 text-violet-700 font-semibold border border-violet-200">
    {value}
  </span>
);

const NotApplicableBadge = () => (
  <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-muted/50 text-muted-foreground font-semibold border border-border">
    N/A
  </span>
);

const ResultDisplay = ({ resultType, value, passed, failed, notApplicable }) => {
  if (value == null)
    return <span className="text-xs px-2.5 py-1 rounded-full bg-muted text-muted-foreground">—</span>;

  if (notApplicable || value === "N/A")
    return <NotApplicableBadge />;

  // Long text value — suppress badge so it renders in the paragraph below
  if (typeof value === "string" && value.length > 60) return null;

  switch (resultType) {
    case "boolean":
      return <BooleanBadge passed={passed} failed={failed} value={value} />;
    case "rating":
      return <RatingDisplay value={value} />;
    case "numeric":
      return <NumericDisplay value={value} />;
    case "enum":
      return <EnumDisplay value={value} />;
    default:
      return <BooleanBadge passed={passed} failed={failed} value={value} />;
  }
};

/* ─── Sub-results (structured output breakdown) ─── */

const isNAValue = (v) => ["not_applicable", "n/a", "na"].includes(String(v || "").toLowerCase());

const SubResultDisplay = ({ result }) => {
  const label = result.name
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
  const isLongText = typeof result.value === "string" && String(result.value).length > 80;
  const hasClassification = result.outcomeClassification && !isNAValue(result.outcomeClassification);

  return (
    <div className={isLongText ? "" : "flex items-center gap-2 flex-wrap"}>
      <span className="text-xs text-muted-foreground font-medium">{label}:</span>
      {isLongText ? (
        <p className="text-xs text-foreground mt-0.5 leading-relaxed">{result.value}</p>
      ) : (
        <ResultDisplay resultType={result.resultType || "boolean"} value={result.value} passed={result.passed} failed={result.failed} notApplicable={result.notApplicable} />
      )}
      {hasClassification && (
        <span className="text-xs text-muted-foreground italic">· {result.outcomeClassification.replace(/_/g, " ")}</span>
      )}
    </div>
  );
};

// Renders the structured-output breakdown for ANY metric that has one (1+ sub-results).
const SubResultsList = ({ subResults }) => {
  if (!subResults || subResults.length === 0) return null;
  return (
    <div className="mt-2 space-y-1.5">
      {subResults.map((r, i) => (
        <SubResultDisplay key={i} result={r} />
      ))}
    </div>
  );
};

/* ─── Status badge ─── */

const StatusBadge = ({ status }) => {
  if (status === "pending" || status === "processing")
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200">
        <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} />
        {status === "processing" ? "Computing" : "Pending"}
      </span>
    );
  if (status === "failed")
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200">
        <AlertTriangle className="h-3 w-3" strokeWidth={2} />
        Failed
      </span>
    );
  if (status === "under_investigation")
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-orange-50 text-orange-600 border border-orange-200">
        <Search className="h-3 w-3" strokeWidth={2} />
        Under Investigation
      </span>
    );
  return null;
};

/* ─── Single-result outcome classification (sub-result classifications are shown
   inline by SubResultsList) ─── */

const MetricsData = ({ subResults, outcomeClassification }) => {
  // When the metric has a sub-results breakdown, classifications render there.
  if (subResults && subResults.length > 0) return null;

  if (outcomeClassification && !isNAValue(outcomeClassification)) {
    return (
      <div className="mt-2">
        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-muted/50 border border-gray-150">
          <span className="text-muted-foreground italic">{outcomeClassification.replace(/_/g, " ")}</span>
        </span>
      </div>
    );
  }

  return null;
};

/**
 * LlmMetricCard — renders a single custom LLM metric result.
 *
 * Display order:
 * 1. Metric Name
 * 2. Result value (Pass/Fail, stars, numeric, enum)
 * 3. Explanation
 * 4. Metrics Data (key: value pairs)
 * 5. Transcript timing (exact second)
 */
const LlmMetricCard = ({
  metricName,
  status,
  resultType = "boolean",
  value: valueProp,
  metricResult,
  explanation,
  transcriptTiming,
  scope,
  isCritical,
  errorMessage,
  computedAt,
  onSeek,
  onMarkIncorrect,
  onRerunMetric,
  metricId,
}) => {
  const navigate = useWsNavigate();
  const isPending = status === "pending" || status === "processing";
  const isFailed = status === "failed";
  const isUnderInvestigation = status === "under_investigation";
  const isComputed = !isPending && !isFailed && !isUnderInvestigation;
  const hasResult = isComputed || isUnderInvestigation;

  // Per-metric actions live in a kebab (⋮) menu, available only once the metric
  // is done — computed or failed — never while pending/in-progress.
  const isDone = status === "computed" || isFailed;
  const canEdit = metricId != null && isDone;
  const canRerun = !!onRerunMetric && metricId != null && isDone;

  // Use the top-level value prop (new format) if available,
  // otherwise fall back to resolving from metricResult (legacy format).
  const resolved = hasResult
    ? valueProp != null
      ? { ...resolveOneValue({ resultType, raw: valueProp }), subResults: metricResult && Array.isArray(metricResult) && metricResult.length > 0 && metricResult[0]?.metric_name ? metricResult.map((item) => ({ name: item.metric_name, outcomeClassification: item.outcome_classification, resultType, ...resolveOneValue({ resultType, raw: item.metric_value }) })) : null, outcomeClassification: metricResult?.[0]?.outcome_classification }
      : resolveResult({ resultType, metricResult })
    : { value: null, passed: null, failed: null, notApplicable: false, subResults: null, outcomeClassification: null };

  const { value, passed, failed, notApplicable, subResults, outcomeClassification } = resolved;

  const canMarkIncorrect = !!onMarkIncorrect && (status === "computed" || notApplicable);
  const showMenu = canEdit || canRerun || canMarkIncorrect;

  // Structured-output breakdown to display (any metric with 1+ sub-results, N/A omitted).
  const visibleSubResults = Array.isArray(subResults) ? subResults.filter((r) => !r.notApplicable) : [];
  const hasSubResults = visibleSubResults.length > 0;

  return (
    <div className="border border-border rounded-lg p-4 hover:border-input transition-colors overflow-hidden">
      {/* Row 1: Name + result value */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <span className="font-medium text-foreground text-sm">{metricName}</span>
          {isCritical && (
            <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-red-50 text-red-600 border border-red-100">
              Critical
            </span>
          )}
          {scope === "global" && (
            <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-orange-50 text-orange-600 border border-orange-100">
              Global
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <StatusBadge status={status} />
          {hasResult && (
            <ResultDisplay
              resultType={resultType}
              value={value}
              passed={passed}
              failed={failed}
              notApplicable={notApplicable}
            />
          )}
          {showMenu && (
            <DropdownMenu>
              <DropdownMenuTrigger
                title="Metric actions"
                className="inline-flex items-center justify-center h-6 w-6 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors focus:outline-none"
              >
                <MoreVertical className="h-4 w-4" strokeWidth={2} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-44">
                {canEdit && (
                  <DropdownMenuItem
                    onClick={() => navigate(`/evaluation/custom-metrics?edit=${metricId}`)}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Edit metric
                  </DropdownMenuItem>
                )}
                {canRerun && (
                  <DropdownMenuItem onClick={() => onRerunMetric(metricId)}>
                    <RotateCw className="h-3.5 w-3.5" />
                    Rerun this metric
                  </DropdownMenuItem>
                )}
                {canMarkIncorrect && (
                  <DropdownMenuItem onClick={onMarkIncorrect}>
                    <ThumbsDown className="h-3.5 w-3.5" />
                    Mark as incorrect
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Long text value (e.g. Call Summary) — shown when badge is suppressed */}
      {hasResult && typeof value === "string" && value.length > 60 && !hasSubResults && (
        <p className="text-sm text-foreground mt-2 leading-relaxed break-words">{value}</p>
      )}

      {/* Structured-output breakdown — every metric that has one (incl. single criterion) */}
      {hasResult && hasSubResults && (
        <SubResultsList subResults={visibleSubResults} />
      )}

      {/* Explanation */}
      {explanation && (
        <p className="text-xs text-muted-foreground mt-2 leading-relaxed break-words">{explanation}</p>
      )}

      {/* Single-result outcome classification (no breakdown) */}
      {hasResult && (
        <MetricsData
          subResults={visibleSubResults}
          outcomeClassification={outcomeClassification}
        />
      )}

      {/* Transcript timing pills */}
      {Array.isArray(transcriptTiming) && transcriptTiming.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {transcriptTiming.map((t, ti) => {
            if (t.start == null) return null;
            const mm = String(Math.floor(t.start / 60)).padStart(2, "0");
            const ss = String(Math.floor(t.start % 60)).padStart(2, "0");
            return (
              <button
                key={ti}
                onClick={() => onSeek?.({ seconds: t.start, id: Date.now() })}
                className="inline-flex items-center gap-1 text-xs px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 cursor-pointer font-medium"
                title={t.text || `Jump to ${mm}:${ss}`}
              >
                <Clock className="h-3 w-3" strokeWidth={2} />
                {mm}:{ss}
                {t.text && (
                  <span className="text-muted-foreground font-normal max-w-[200px] truncate">
                    — {t.text}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Error message */}
      {isFailed && errorMessage && (
        <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3 shrink-0" />
          {errorMessage}
        </p>
      )}

      {/* Computed timestamp */}
      {computedAt && hasResult && (
        <p className="text-[10px] text-muted-foreground mt-2">
          Computed {new Date(computedAt).toLocaleString()}
        </p>
      )}
    </div>
  );
};

export default LlmMetricCard;
